import { Col, Container, Row, Spacer, View } from "@amzn/stencil-react-components/layout";
import { H1, Text } from "@amzn/stencil-react-components/text";
import { TabBar, TabPanel, TabSwitcher, useTabs } from "@amzn/stencil-react-components/tabs";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import React, { FC } from "react";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { useHssStatus } from "@/hooks/use-hss-status";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { UserPermissions } from "@/services/user-permissions/user-permissions";
import { CampaignResponseForm } from "./components/campaign-response-form";
import { CampaignStatusChip } from "./components/campaign-status-chip";
import { TeamCampaignsSection } from "./components/team-campaigns-section";

const MY_CAMPAIGN_TAB = "my-campaign";
const TEAM_CAMPAIGNS_TAB = "team-campaigns";

const CampaignsPage: FC = () => {
  const { data, isLoading, error } = useHssStatus();
  const currentUsername = UserPermissions.current.username ?? "";

  const myStatus = data?.myStatus;
  const campaign = myStatus?.campaign;
  const directReports = data?.directReports ?? [];
  const hasDirectReports = directReports.length > 0;

  const tabs = [
    { value: MY_CAMPAIGN_TAB, label: getString("campaigns.myCampaignTab") },
    ...(hasDirectReports
      ? [{ value: TEAM_CAMPAIGNS_TAB, label: getString("campaigns.teamCampaignsTab") }]
      : []),
  ];
  const { tabBarProps, tabSwitcherProps } = useTabs({ tabs });

  useDocumentTitle(getString("campaigns.pageHeading"));

  if (isLoading) {
    return (
      <Container isFullWidth={false}>
        <View padding="S600" display="flex" justifyContent="center">
          <Spacer height="S200" />
          <Spinner size={SpinnerSize.Medium} />
        </View>
      </Container>
    );
  }

  if (error) {
    return (
      <Container isFullWidth={false}>
        <View padding="S600">
          <Spacer height="S200" />
          <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
            {getString("campaigns.error")}
          </MessageBanner>
        </View>
      </Container>
    );
  }

  return (
    <Container isFullWidth={false}>
      <View padding="S600">
        <Col gridGap="S400" width="100%">
          <Col gridGap="S200">
            <H1>{getString("campaigns.pageHeading")}</H1>
            <TabBar {...tabBarProps} />
          </Col>
          <TabSwitcher {...tabSwitcherProps}>
            <TabPanel value={MY_CAMPAIGN_TAB}>
              <View
                style={{ padding: "16px 16px 15px 16px" }}
                border="1px solid var(--st-dt-color-neutral-20)"
                borderRadius="R400"
              >
                <Col gridGap="S300">
                  <Row gridGap="S300" alignItems="center">
                    {campaign && (
                      <Text fontSize="20px" fontWeight="bold">
                        {campaign.name}
                      </Text>
                    )}
                    <CampaignStatusChip campaign={campaign} />
                  </Row>
                  <CampaignResponseForm
                    campaign={campaign}
                    assets={myStatus?.assets ?? []}
                    existingResponses={myStatus?.responses ?? {}}
                    login={currentUsername}
                    currentUsername={currentUsername}
                  />
                </Col>
              </View>
            </TabPanel>
            {hasDirectReports && (
              <TabPanel value={TEAM_CAMPAIGNS_TAB}>
                <TeamCampaignsSection
                  directReports={directReports}
                  currentUsername={currentUsername}
                />
              </TabPanel>
            )}
          </TabSwitcher>
        </Col>
      </View>
    </Container>
  );
};

export default CampaignsPage;
